import { ActualServer, API_BASE_URL, BudgetAllocationApplyResult, BudgetAllocationItem, BudgetAllocationSuggestion, BudgetMonthData } from '../types';
import { auth } from './firebase';

export interface CategoryPayload {
  name: string;
  /** Actual Budget category note (e.g. `#template 500`) */
  note?: string;
}

export interface CategoryGroupPayload {
  name: string;
  categories: Array<string | CategoryPayload>;
  /** Create as an Actual income category group */
  isIncome?: boolean;
}

export interface AccountPayload {
  name: string;
  accountType?: string;
}

export interface CreateCategoriesResult {
  createdGroups: number;
  createdCategories: number;
  skippedCategories: number;
  notesApplied?: number;
  deletedGroups?: number;
}

export interface CreateAccountsResult {
  createdAccounts: number;
  skippedAccounts: number;
}

/**
 * Create category groups and categories in the server's default budget.
 * When replaceExisting is true, all existing category groups are removed first.
 */
export async function createCategoriesInBudget(
  server: ActualServer,
  groups: CategoryGroupPayload[],
  options: { replaceExisting?: boolean } = {}
): Promise<CreateCategoriesResult> {
  const token = await auth.currentUser?.getIdToken();
  if (!token) {
    throw new Error('AUTH_REQUIRED');
  }
  if (!server.defaultBudgetId) {
    throw new Error('NO_DEFAULT_BUDGET');
  }

  const response = await fetch(`${API_BASE_URL}/create-categories`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      serverURL: server.url,
      password: server.password,
      budgetId: server.defaultBudgetId,
      budgetPassword: server.defaultBudgetPassword,
      groups,
      replaceExisting: options.replaceExisting ?? false
    })
  });

  if (!response.ok) {
    let errorMessage = 'REQUEST_FAILED';
    try {
      const errorData = await response.json();
      if (errorData.error) {
        errorMessage = errorData.error;
      }
    } catch {
      // Keep generic error code
    }
    throw new Error(errorMessage);
  }

  const data = await response.json();
  return {
    createdGroups: data.createdGroups ?? 0,
    createdCategories: data.createdCategories ?? 0,
    skippedCategories: data.skippedCategories ?? 0,
    notesApplied: data.notesApplied ?? 0,
    deletedGroups: data.deletedGroups ?? 0
  };
}

/**
 * Create bank accounts in the server's default budget (separate from categories).
 */
export async function createAccountsInBudget(
  server: ActualServer,
  accounts: AccountPayload[]
): Promise<CreateAccountsResult> {
  const token = await auth.currentUser?.getIdToken();
  if (!token) {
    throw new Error('AUTH_REQUIRED');
  }
  if (!server.defaultBudgetId) {
    throw new Error('NO_DEFAULT_BUDGET');
  }

  const response = await fetch(`${API_BASE_URL}/create-accounts`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      serverURL: server.url,
      password: server.password,
      budgetId: server.defaultBudgetId,
      budgetPassword: server.defaultBudgetPassword,
      accounts
    })
  });

  if (!response.ok) {
    let errorMessage = 'REQUEST_FAILED';
    try {
      const errorData = await response.json();
      if (errorData.error) {
        errorMessage = errorData.error;
      }
    } catch {
      // Keep generic error code
    }
    throw new Error(errorMessage);
  }

  const data = await response.json();
  return {
    createdAccounts: data.createdAccounts ?? 0,
    skippedAccounts: data.skippedAccounts ?? 0
  };
}

export interface FetchBudgetMonthResult {
  budgetMonth: BudgetMonthData;
  availableMonths: string[];
  month: string;
}

/**
 * Fetch budget month data (category groups, allocations, spent, balances).
 * If the requested month has no sheet, the API resolves to the nearest available month.
 */
export async function fetchBudgetMonth(
  server: ActualServer,
  month?: string
): Promise<FetchBudgetMonthResult> {
  const token = await auth.currentUser?.getIdToken();
  if (!token) {
    throw new Error('AUTH_REQUIRED');
  }
  if (!server.defaultBudgetId) {
    throw new Error('NO_DEFAULT_BUDGET');
  }

  const response = await fetch(`${API_BASE_URL}/get-budget-month`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      serverURL: server.url,
      password: server.password,
      budgetId: server.defaultBudgetId,
      budgetPassword: server.defaultBudgetPassword,
      month
    })
  });

  if (!response.ok) {
    let errorMessage = 'REQUEST_FAILED';
    try {
      const errorData = await response.json();
      if (errorData.code === 'MONTH_NOT_FOUND' || errorData.code === 'NO_BUDGET_MONTHS') {
        throw new Error(errorData.code);
      }
      if (errorData.error) {
        errorMessage = errorData.error;
      }
      if (errorData.needsBudgetPassword) {
        throw new Error('NEEDS_BUDGET_PASSWORD');
      }
    } catch (err) {
      if (
        err instanceof Error &&
        (err.message === 'NEEDS_BUDGET_PASSWORD' ||
          err.message === 'MONTH_NOT_FOUND' ||
          err.message === 'NO_BUDGET_MONTHS')
      ) {
        throw err;
      }
    }
    throw new Error(errorMessage);
  }

  const data = await response.json();
  const budgetMonth = data.budgetMonth as BudgetMonthData;
  const availableMonths = Array.isArray(data.availableMonths)
    ? (data.availableMonths as string[])
    : [];
  const resolvedMonth =
    (typeof data.month === 'string' && data.month) ||
    budgetMonth?.month ||
    month ||
    '';

  return {
    budgetMonth,
    availableMonths,
    month: resolvedMonth
  };
}

/**
 * Set the budgeted amount for a single category (integer cents).
 * Returns the refreshed month data from Actual Budget.
 */
export async function updateCategoryBudget(
  server: ActualServer,
  month: string,
  categoryId: string,
  amountCents: number
): Promise<BudgetMonthData> {
  const token = await auth.currentUser?.getIdToken();
  if (!token) {
    throw new Error('AUTH_REQUIRED');
  }
  if (!server.defaultBudgetId) {
    throw new Error('NO_DEFAULT_BUDGET');
  }

  const response = await fetch(`${API_BASE_URL}/set-budget-amount`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify({
      serverURL: server.url,
      password: server.password,
      budgetId: server.defaultBudgetId,
      budgetPassword: server.defaultBudgetPassword,
      month,
      categoryId,
      amountCents
    })
  });

  if (!response.ok) {
    let errorMessage = 'REQUEST_FAILED';
    try {
      const errorData = await response.json();
      if (errorData.error) {
        errorMessage = errorData.error;
      }
      if (errorData.needsBudgetPassword) {
        throw new Error('NEEDS_BUDGET_PASSWORD');
      }
    } catch (err) {
      if (err instanceof Error && err.message === 'NEEDS_BUDGET_PASSWORD') {
        throw err;
      }
    }
    throw new Error(errorMessage);
  }

  const data = await response.json();
  return data.budgetMonth as BudgetMonthData;
}

export async function suggestBudgetAllocation(
  server: ActualServer,
  month?: string
): Promise<BudgetAllocationSuggestion> {
  const token = await auth.currentUser?.getIdToken();
  if (!token) {
    throw new Error('AUTH_REQUIRED');
  }
  if (!server.defaultBudgetId) {
    throw new Error('NO_DEFAULT_BUDGET');
  }

  const response = await fetch(`${API_BASE_URL}/suggest-budget-allocation`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify({
      serverURL: server.url,
      password: server.password,
      budgetId: server.defaultBudgetId,
      budgetPassword: server.defaultBudgetPassword,
      month
    })
  });

  if (!response.ok) {
    let errorMessage = 'REQUEST_FAILED';
    let errorCode: string | undefined;
    try {
      const errorData = await response.json();
      if (errorData.error) errorMessage = errorData.error;
      if (errorData.code) errorCode = errorData.code;
    } catch {
      // keep generic
    }
    const err = new Error(errorMessage) as Error & { code?: string };
    err.code = errorCode;
    throw err;
  }

  const data = await response.json();
  return data as BudgetAllocationSuggestion;
}

export async function repromptBudgetAllocation(
  server: ActualServer,
  month: string,
  userFeedback: string,
  previousAllocations: BudgetAllocationItem[]
): Promise<BudgetAllocationSuggestion> {
  const token = await auth.currentUser?.getIdToken();
  if (!token) {
    throw new Error('AUTH_REQUIRED');
  }

  const response = await fetch(`${API_BASE_URL}/reprompt-budget-allocation`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify({
      serverURL: server.url,
      password: server.password,
      budgetId: server.defaultBudgetId,
      budgetPassword: server.defaultBudgetPassword,
      month,
      userFeedback,
      previousAllocations
    })
  });

  if (!response.ok) {
    let errorMessage = 'REQUEST_FAILED';
    try {
      const errorData = await response.json();
      if (errorData.error) errorMessage = errorData.error;
    } catch {
      // keep generic
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

export async function applyBudgetAllocation(
  server: ActualServer,
  month: string,
  source: 'goals' | 'ai',
  allocations: BudgetAllocationItem[]
): Promise<BudgetAllocationApplyResult> {
  const token = await auth.currentUser?.getIdToken();
  if (!token) {
    throw new Error('AUTH_REQUIRED');
  }

  const response = await fetch(`${API_BASE_URL}/apply-budget-allocation`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify({
      serverURL: server.url,
      password: server.password,
      budgetId: server.defaultBudgetId,
      budgetPassword: server.defaultBudgetPassword,
      month,
      source,
      allocations
    })
  });

  if (!response.ok) {
    let errorMessage = 'REQUEST_FAILED';
    try {
      const errorData = await response.json();
      if (errorData.error) errorMessage = errorData.error;
    } catch {
      // keep generic
    }
    throw new Error(errorMessage);
  }

  return response.json();
}
